import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../api/core";
import { useEditItem2 } from "../../../api/useNewItem";
import { useLoadedData } from "../../../hooks/useLoadedData";
import { useSmartList } from "../../../hooks/useSmartList";
import { Contact, ContactExtraInfo, ContactOrgLink, ContactSchema } from "../types";
import { useAction, useItemActionWithConfirmation } from "../../../api/useAction";
import { useImportContacts } from "./useImportContacts";
import { useContactMerge } from "./useContactsMerge";
import { useExportContacts } from "./useExportContacts";
import { useUnifyContactsGeoNames } from "./useUnifyContactsGeoNames";
import { useContactNotes } from "./useContactNotes";
import { createSelectSchema, FieldType, mergeSchema } from "../../../hooks/useSchema";
import { useContactsBatchUpdate } from "./useContactsBatchUpdate";
import { useCRMContext } from "../CRMContext";
import { base64encodeUrl } from "../../../api/base64";
import { useThrottledCapture } from "../../primitives";

export interface IFilters {
  tags?: number[];
  categories?: string[];
  mode?: 'AND' | 'OR';
  geo_country?: string[];
  geo_city?: string[];
  has_phone?: 'true' | 'false' | '';
  has_email?: 'true' | 'false' | '';
  do_not_contact?: 'include' | 'exclude' | '';
  consent_pd?: string[];
  consent_emails?: string[];
  languages?: string[];
  email_search?: string;
  attributes?: any[];
}

export const ContactFiltersSchema = mergeSchema(ContactSchema, {
  email_search: { label: "Поиск по e-mail" },
  consent_pd: { type: FieldType.multiselect },
  consent_emails: { type: FieldType.multiselect },
  languages: ContactSchema.communication_language,
});

export const DefaultFitlers: IFilters = {};

export const useContactsList = (apiPath: string) => {
  const contactsApiPath = `${apiPath}/contact`;
  const [extraFilters, setExtraFilters] = useState<IFilters>(DefaultFitlers);
  const extraFiltersThrottle = useThrottledCapture(extraFilters, 500);

  //
  // SELECTION
  //
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);

  const selectContact = useCallback((contact: Contact) => {
    setSelectedContacts(prev => {
      if (prev.some(c => c._id === contact._id)) return prev;
      return [...prev, contact];
    });
  }, []);

  const deselectContact = useCallback((contactIdOrContact: string | Contact) => {
    const id = typeof contactIdOrContact === 'string' ? contactIdOrContact : contactIdOrContact._id;
    setSelectedContacts(prev => prev.filter(c => c._id !== id));
  }, []);

  const toggleContact = useCallback((contact: Contact) => {
    setSelectedContacts(prev => {
      const exists = prev.some(c => c._id === contact._id);
      return exists ? prev.filter(c => c._id !== contact._id) : [...prev, contact];
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedContacts([]);
  }, []);

  const isContactSelected = useCallback(
    (contactId: string) => selectedContacts.some(c => c._id === contactId),
    [selectedContacts]
  );

  const ctx = useCRMContext();
  const schema = useMemo(() => {
    return mergeSchema(ContactSchema, {
      categories: createSelectSchema(
        (ctx.settings.contact_categories || []).map(x => ({ value: x.code, label: x.label })),
        { type: FieldType.multiselect }
      ),
    });
  }, [ctx.settings.contact_categories]);

  const data = useSmartList<Contact>(`${contactsApiPath}`, {
    newRecordDefault: {},
    getRecordApiPath: r => `${contactsApiPath}/${r._id}`,
    editRecordSettings: {
      dontResetOnSave: true,
    },
    schema,
    noView: true,
    textFilterApi: {
      paramName: "search",
    },
    extraParams: {
      tags: extraFilters.tags?.length ? extraFilters.tags : undefined,
      tags_mode: extraFilters.mode || undefined,
      categories: extraFilters.categories?.length ? extraFilters.categories.join(",") : undefined,
      geo_country: extraFilters.geo_country?.length ? extraFilters.geo_country.join(',') : undefined,
      geo_city: extraFilters.geo_city?.length ? extraFilters.geo_city.join(',') : undefined,
      consent_pd: extraFilters.consent_pd?.length ? extraFilters.consent_pd.join(",") : undefined,
      consent_emails: extraFilters.consent_emails?.length ? extraFilters.consent_emails.join(",") : undefined,
      languages: extraFilters.languages?.length ? extraFilters.languages.join(",") : undefined,
      has_phone: extraFilters.has_phone || undefined,
      has_email: extraFilters.has_email || undefined,
      do_not_contact: extraFilters.do_not_contact || undefined,
      email_search: extraFiltersThrottle.email_search || undefined,
      "attributes-filters": extraFiltersThrottle.attributes?.length
        ? base64encodeUrl(JSON.stringify(extraFiltersThrottle.attributes))
        : undefined,
    },
    skipTextFilter: true,
  });

  // 
  // CREATING
  // 
  const newRecord = useEditItem2<Partial<Contact>>({
    save: async (item: Partial<Contact>) => {
      const saved = { ...item };
      delete saved.email;
      delete saved.phone;
      delete saved.organization_link;
      if (item.email) {
        saved.emails = [{ value: item.email }];
      }
      if (item.phone) {
        saved.phones = [{ value: item.phone }];
      }

      const result = await apiFetch<Partial<Contact>>(contactsApiPath, "post", saved);
      if (item.organization_link?.organization_id) {
        await apiFetch<any>(`${contactsApiPath}/${result._id}/organization`, "post", {
          organization_id: item.organization_link.organization_id,
          title: item.organization_link.title,
          is_active: item.organization_link.is_active,
        });
      }
      data.reload();
      data.editRecord.startEditing({ ...result } as any);
      return result;
    }
  });

  // 
  // EDITING
  // 
  const editedContactInfo = useLoadedData<ContactExtraInfo | null>(
    `${contactsApiPath}/${data.editRecord.item?._id}/info`,
    null,
    !!data.editRecord.item?._id
  );

  const notes = useContactNotes(
    data.editRecord.item?._id,
    editedContactInfo.data?.notes,
    editedContactInfo.reload,
    apiPath
  );

  const addOrEditOrgLink = useEditItem2<Partial<ContactOrgLink>>({
    save: async (item, changes) => {
      let res = null;
      if (item._id) {
        const saved = {
          is_active: changes.is_active,
          title: changes.title,
        };
        res = await apiFetch(`${contactsApiPath}/${item.contact_id}/organization/${item._id}`, "put", saved);
      } else {
        const saved = {
          is_active: item.is_active,
          organization_id: item.organization_id,
          title: item.title,
        };
        res = await apiFetch(`${contactsApiPath}/${data.editRecord.item?._id}/organization`, "post", saved);
      }
      editedContactInfo.reload();
      return res as any;
    },
  });

  const removeOrgLink = useItemActionWithConfirmation<ContactOrgLink, {}>(
    async (item) => {
      await apiFetch(`${contactsApiPath}/${item.contact_id}/organization/${item._id}`, "delete");
      editedContactInfo.reload();
      if (addOrEditOrgLink.item?._id === item._id) {
        addOrEditOrgLink.cancel();
      }
      return {};
    },
    { title: "Отвязать", memoTrigger: `${data.editRecord.item?._id}` }
  );

  useEffect(() => {
    if (!data.editRecord.item?._id) {
      editedContactInfo.setData(null);
      addOrEditOrgLink.cancel();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.editRecord.item?._id]);

  // 
  // AUX
  // 
  const findContact = (id: string) => apiFetch<Contact>(`${contactsApiPath}/${id}`);

  // 
  // OPERATIONS
  // 
  const importContacts = useImportContacts();
  const exportContacts = useExportContacts();

  const startExportContacts = useAction(async () => {
    if (selectedContacts.length > 0) {
      exportContacts.open(selectedContacts);
    } else {
      const contacts = await apiFetch<Contact[]>(`${data.apiPath}?${data.queryNoLimit}`, "get");
      exportContacts.open(contacts);
    }
  });

  const merge = useContactMerge(c => data.editRecord.startEditing({ ...c }));
  const unifyGeo = useUnifyContactsGeoNames(() => data.reload());

  const batchUpdate = useContactsBatchUpdate(() => {
    data.reload();
    clearSelection();
  });

  const startBatchUpdate = useAction(async () => {
    if (selectedContacts.length > 0) {
      batchUpdate.open(selectedContacts.map(x => x._id));
    } else {
      const contacts = await apiFetch<Contact[]>(`${data.apiPath}?${data.queryNoLimit}&only-id=true`, "get");
      batchUpdate.open(contacts.map(x => x._id));
    }
  });

  return {
    ...data,
    extraFilters,
    setExtraFilters,
    clearExtraFilters: () => setExtraFilters(DefaultFitlers),

    selection: {
      items: selectedContacts,
      count: selectedContacts.length,
      select: selectContact,
      deselect: deselectContact,
      toggle: toggleContact,
      clear: clearSelection,
      isSelected: isContactSelected,
    },

    newRecord: {
      ...newRecord,
      startEditing: () => {
        data.editRecord.cancel();
        newRecord.startEditing({});
      },
    },
    editRecord: {
      ...data.editRecord,
      extraInfo: editedContactInfo,
      notes,
      addOrEditOrgLink,
      removeOrgLink,
    },

    findContact,
    importContacts,
    exportContacts: {
      ...exportContacts,
      start: startExportContacts,
    },
    merge,
    unifyGeo,
    batchUpdate: {
      ...batchUpdate,
      start: startBatchUpdate,
    },
  };
};

export type ContactsListData = ReturnType<typeof useContactsList>;